/**
 * /orgs/:orgId/overview — the one tenant home (Feature 70, D3).
 *
 * The record and the workspace landing were two pages; an operator arrived
 * on one and had to find the other. This is both: the detail header with
 * the decision the status calls for, the counts as the way into each
 * section, the recent tasks, and the organization's own settings below the
 * fold. Every section is still its own nav entry; this page does not repeat
 * them, it points at them.
 */
import { listAgents } from '@optum/api-client/endpoints/agents';
import { listOrgCollections } from '@optum/api-client/endpoints/collections';
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';
import {
  listGroupCollections,
  listGroupMembers,
  listGroups,
} from '@optum/api-client/endpoints/groups';
import { listOrgSecrets } from '@optum/api-client/endpoints/organization-secrets';
import { getAdminOrganization } from '@optum/api-client/endpoints/organizations';
import { listTasks } from '@optum/api-client/endpoints/tasks';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext.js';
import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { LoadingBlock } from '../../ui/AsyncState.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { absoluteDate, absoluteDateTime, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useWindowStart } from '../../lib/use-time-window.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { Band } from '../../ui/Band.js';
import { RecordShape, ShapeError, ShapeLoading } from '../../ui/shapes.js';
import { DataTable } from '../../ui/DataTable.js';
import { Avatar, Badge, Icon, KeyValue, OnboardingChecklist, Typography } from '../../ui/finixt.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { OrgActions } from '../organizations/OrgActions.js';
import { OrgSettings } from '../organizations/OrgSettings.js';
import { orgStatusLabel } from '../organizations/org-status.js';
import { RegistrationActions } from '../registrations/review-actions.js';
import { useOnboardingSteps } from './onboarding-steps.js';
import { buildTenantNeeds } from './tenant-needs.js';
import { AutomationSummary } from '../automation/AutomationSummary.js';
import { LinkedTaskCell } from '../tasks/LinkedTaskCell.js';
import { NESTED_ROW_STYLE, linkedByParent, withLinked } from '../tasks/linked-rows.js';
import { TenantNeeds } from './TenantNeeds.js';

const DAY_MS = 24 * 60 * 60 * 1000;

export function OrgOverviewPage() {
  const orgId = useRequiredOrgId();
  const navigate = useNavigate();
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const { isPlatformAdmin } = useAuth();

  const org = useQuery({ queryKey: qk.org(orgId), queryFn: () => getAdminOrganization(orgId) });
  const agents = useQuery({
    queryKey: qk.agents(orgId),
    queryFn: () => listAgents({ organization_id: orgId }),
  });
  const collections = useQuery({
    queryKey: qk.collections(orgId),
    queryFn: () => listOrgCollections(orgId),
  });
  const groups = useQuery({ queryKey: qk.groups(orgId), queryFn: () => listGroups(orgId) });
  const secrets = useQuery({
    queryKey: qk.orgSecrets(orgId),
    queryFn: () => listOrgSecrets(orgId),
  });
  // A group that grants paths but has no member strands those documents.
  // Knowing that takes each group's members and grants, the same two reads
  // the Groups page makes per card; bounded by the tenant's group count.
  const groupList = groups.data ?? [];
  const reach = useQueries({
    queries: groupList.flatMap((g) => [
      {
        queryKey: qk.groupMembers(orgId, g.id),
        queryFn: () => listGroupMembers(orgId, g.id),
      },
      {
        queryKey: qk.groupCollections(orgId, g.id),
        queryFn: () => listGroupCollections(orgId, g.id),
      },
    ]),
  });
  // Names the partner whose endpoint would receive this tenant's deliveries
  // while it has none of its own, and draws it on the header.
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
    enabled: Boolean(org.data?.federated_partner_id),
  });
  const since = useWindowStart(DAY_MS);
  const tasks = useQuery({
    queryKey: qk.tasks({ organization_id: orgId, created_after: since }),
    queryFn: () => listTasks({ organization_id: orgId, created_after: since, limit: 200 }),
  });
  const onboarding = useOnboardingSteps(orgId);
  useBreadcrumbTail(org.data?.name);

  if (org.isPending) {
    return <ShapeLoading shape="record" />;
  }
  if (org.isError) {
    return <ShapeError shape="record" error={org.error} fallback="Failed to load tenant" />;
  }

  const o = org.data;
  const partnerName =
    (partners.data ?? []).find((p) => p.id === o.federated_partner_id)?.name ?? null;
  const failed = (tasks.data ?? []).filter((t) => t.status === 'failed').length;
  // Linked tasks sit under their first task, from the same day's tasks (Feature 71, D33).
  const firstTasks = (tasks.data ?? []).filter((t) => !t.parent_task_id);
  const recentFirst = firstTasks
    .slice()
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, 8);
  const recent = withLinked(recentFirst, linkedByParent(tasks.data ?? []));
  const selectedTask = panel.current?.kind === 'task' ? panel.current.id : undefined;
  const unreachableGroups = groupList.flatMap((g, i) => {
    const members = reach[i * 2]?.data as unknown[] | undefined;
    const grants = reach[i * 2 + 1]?.data as unknown[] | undefined;
    if (!members || !grants) return [];
    return grants.length > 0 && members.length === 0
      ? [{ id: g.id, name: g.name, collections: grants.length }]
      : [];
  });
  const needs = buildTenantNeeds({
    orgId,
    failedTasks: failed,
    migrationPaths: (collections.data?.nodes ?? [])
      .filter((n) => n.status === 'needs_migration')
      .map((n) => n.path),
    unreachableGroups,
    onboarding: onboarding.loading ? null : { done: onboarding.done, total: onboarding.total },
  });
  const needsLoading =
    tasks.isPending || collections.isPending || groups.isPending || reach.some((q) => q.isPending);
  const capabilityCount = new Set((agents.data ?? []).flatMap((a) => a.capabilities)).size;
  const meta = [
    `created ${absoluteDate(o.created_at)}`,
    ...(o.activated_at ? [`activated ${absoluteDate(o.activated_at)}`] : []),
    ...(o.approved_by ? [`approved by ${o.approved_by}`] : []),
  ].join(' · ');

  return (
    <RecordShape
      avatar={<Avatar initials={o.name.slice(0, 2).toUpperCase()} size={48} shape="square" />}
      eyebrow={
        <>
          <Typography variant="eyebrow">Organization</Typography>
          <StatusBadge
            status={o.status}
            label={orgStatusLabel(o.status)}
            vocabulary="organization"
          />
          {partnerName ? <Badge tone="neutral">{partnerName}</Badge> : null}
        </>
      }
      title={o.name}
      subtitle={
        <Typography variant="meta" mono as="span">
          {o.id}
        </Typography>
      }
      meta={meta}
      action={
        o.status === 'pending' ? (
          <RegistrationActions reg={o} canMutate={isPlatformAdmin} />
        ) : isPlatformAdmin ? (
          <OrgActions id={o.id} name={o.name} status={o.status} />
        ) : null
      }
    >
      {o.rejected_reason ? (
        <p style={{ marginTop: 11, fontSize: 'var(--text-body-sm)', color: 'var(--danger)' }}>
          Rejected: {o.rejected_reason}
        </p>
      ) : null}

      <div
        style={{
          marginTop: 22,
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 9,
        }}
      >
        <Tile
          icon="smart_toy"
          label="Agents"
          value={count(agents.data?.length, agents.isPending)}
          tone="var(--blue)"
          onOpen={() => navigate(`/orgs/${orgId}/agents`)}
        />
        <Tile
          icon="folder_open"
          label="Collections"
          value={count(collections.data?.nodes.length, collections.isPending)}
          tone="var(--info)"
          onOpen={() => navigate(`/orgs/${orgId}/collections`)}
        />
        <Tile
          icon="shield_person"
          label="Groups"
          value={count(groups.data?.length, groups.isPending)}
          tone="var(--violet)"
          onOpen={() => navigate(`/orgs/${orgId}/groups`)}
        />
        <Tile
          icon="settings"
          label="Capabilities"
          value={count(agents.data ? capabilityCount : undefined, agents.isPending)}
          tone="var(--blue)"
          onOpen={() => navigate(`/orgs/${orgId}/config`)}
        />
        <Tile
          icon="key"
          label="Secrets set"
          value={count(secrets.data?.filter((s) => s.is_set).length, secrets.isPending)}
          tone="var(--warn)"
          onOpen={() => navigate(`/orgs/${orgId}/secrets`)}
        />
      </div>

      <div
        style={{
          marginTop: 7,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2.2fr) minmax(0, 1fr)',
          gap: 18,
          alignItems: 'start',
        }}
      >
        <div>
          <Band label="Needs you" style={{ marginTop: 22, marginBottom: 11 }} />
          <TenantNeeds rows={needs} loading={needsLoading} />

          <AutomationSummary orgId={orgId} />

          <Band label="Recent tasks" style={{ marginTop: 30, marginBottom: 11 }} />
          {tasks.isPending && <LoadingBlock />}
          {tasks.isSuccess && recent.rows.length === 0 && (
            <div
              style={{
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border-hairline)',
                background: 'var(--surface-card)',
                padding: 22,
                textAlign: 'center',
              }}
            >
              <Icon name="timeline" size={22} style={{ color: 'var(--text-ghost)' }} />
              <p
                style={{
                  marginTop: 7,
                  fontSize: 'var(--text-body-md)',
                  color: 'var(--text-muted)',
                }}
              >
                Nothing has run for this tenant in the last 24 hours.
              </p>
            </div>
          )}
          {recent.rows.length > 0 && (
            <DataTable
              count={`${recentFirst.length} of ${firstTasks.length} in the last 24h`}
              hint={failed > 0 ? `${failed} failed` : 'no failures'}
              rows={recent.rows}
              selectedKey={selectedTask}
              rowStyle={(t) =>
                recent.nested.has(t.id) && t.id !== selectedTask ? NESTED_ROW_STYLE : undefined
              }
              onRowClick={(t) => panel.open({ kind: 'task', id: t.id })}
              onRowOpenPage={(t) => openPage({ kind: 'task', id: t.id })}
              columns={[
                {
                  key: 'id',
                  label: 'Task',
                  mono: true,
                  strong: true,
                  render: (t) => <LinkedTaskCell task={t} depth={recent.depth.get(t.id) ?? 0} />,
                },
                { key: 'capability_id', label: 'Capability' },
                {
                  key: 'status',
                  label: 'Status',
                  render: (t) => <StatusBadge status={t.status} />,
                },
                {
                  key: 'created_at',
                  label: 'When',
                  align: 'right',
                  mono: true,
                  render: (t) => absoluteDateTime(t.created_at),
                },
              ]}
            />
          )}
        </div>
        <div>
          <Band label="Facts" style={{ marginTop: 22, marginBottom: 11 }} />
          <SectionCard>
            <SectionCardBody>
              <KeyValue
                dense
                labelWidth={104}
                items={[
                  { label: 'Contact', value: o.contact_email ?? '—', mono: false },
                  {
                    label: 'Partner',
                    value:
                      partnerName ??
                      (o.federated_partner_id ? shortId(o.federated_partner_id) : 'none'),
                    mono: !partnerName,
                  },
                  ...(o.federated_external_id
                    ? [{ label: 'Partner org id', value: o.federated_external_id }]
                    : []),
                  { label: 'Updated', value: absoluteDate(o.updated_at) },
                  {
                    label: 'Concurrency',
                    value:
                      o.max_concurrent_tasks == null
                        ? 'platform default'
                        : String(o.max_concurrent_tasks),
                    mono: o.max_concurrent_tasks != null,
                  },
                ]}
              />
            </SectionCardBody>
          </SectionCard>
          {/* The card is the checklist until the tenant is set up end to end;
              at six of six it is not drawn (D10). */}
          {!onboarding.loading && onboarding.done < onboarding.total ? (
            <div id="onboarding" style={{ marginTop: 22 }}>
              <OnboardingChecklist
                title="Onboarding"
                subtitle={`${onboarding.done} of ${onboarding.total}`}
                steps={onboarding.steps.map((s) => ({
                  id: String(s.n),
                  label: s.title,
                  done: s.done,
                  body: s.description,
                  meta: s.rows[0]?.stateLabel,
                  actionLabel: s.cta,
                  onAction: () => navigate(s.to),
                }))}
              />
            </div>
          ) : null}
        </div>
      </div>

      <OrgSettings org={o} canMutate={isPlatformAdmin} partnerName={partnerName} />
    </RecordShape>
  );
}

/** A count that is also the way in — the number and the destination are one. */
function Tile({
  icon,
  label,
  value,
  tone,
  onOpen,
}: {
  icon: string;
  label: string;
  value: string;
  tone: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 16,
        textAlign: 'left',
      }}
    >
      <span style={{ marginBottom: 11, display: 'flex', alignItems: 'center', gap: 7 }}>
        <Icon name={icon} size={15} color={tone} />
        <Typography variant="eyebrow">{label}</Typography>
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 'var(--text-page)',
          lineHeight: 1,
          fontWeight: 'var(--weight-bold)',
          letterSpacing: '-0.025em',
          color: 'var(--text-strong)',
        }}
        className="mono"
      >
        {value}
      </span>
    </button>
  );
}

function count(n: number | undefined, loading: boolean): string {
  if (loading) return '…';
  if (n === undefined) return '—';
  return n.toLocaleString();
}
